import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { SupporterIcon } from 'images';
import { numberWithCommas, Colors, device } from 'utils';
import DonationButton from 'utils/DonationButton';
import PersonIcon from './Icons/PersonIcon';
import TimeIcon from './Icons/TimeIcon';
import ProgressBar from '../helpers/ProgressBar';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_adgroup',
  'utm_placement',
  'utm_device',
  'utm_audience',
  'utm_geo',
  'utm_creative',
  'utm_network',
  'utm_id',
];

function buildProjectUrl(url) {
  if (!url) return '/project/iiiev';

  const params = new URLSearchParams();

  UTM_KEYS.forEach((key) => {
    const rawValue = sessionStorage.getItem(key);

    if (
      rawValue !== null &&
      rawValue !== undefined &&
      rawValue !== 'null' &&
      rawValue !== 'undefined' &&
      String(rawValue).trim() !== ''
    ) {
      params.set(key, rawValue);
    }
  });

  const queryString = params.toString();
  return `/project/${url}${queryString ? `?${queryString}` : ''}`;
}

function formatMoney(value) {
  return numberWithCommas(Number(value || 0).toFixed(2));
}

export default function MoreProjectCard({ project, isSuccessful }) {
  const {
    category,
    name,
    target,
    fundings,
    days,
    url,
    from,
    photo,
    is_patreon,
    is_ticketable,
    donations_count,
    id,
    photo_variant,
    status,
  } = project;

  const percentage = useMemo(() => {
    const safeTarget = Number(target || 0);
    const safeFundings = Number(fundings || 0);

    if (!safeTarget) return 0;
    return Math.round((safeFundings / safeTarget) * 100);
  }, [fundings, target]);

  const finalUrl = useMemo(() => buildProjectUrl(url), [url]);

  const imageSrc = photo_variant ?? photo;
  const showDonationButton =
    days !== 0 && !is_ticketable && status !== 'finished' && !isSuccessful;

  return (
    <OuterWrapper>
      <CardWrapper>
        <TopLink to={finalUrl}>
          <ImageWrapper>
            <Image src={imageSrc} alt={name} />
            {/*{category ? <CategoryBadge>{category}</CategoryBadge> : null}*/}
          </ImageWrapper>
        </TopLink>

        <Content>
          <TitleLink to={finalUrl}>
            <Title title={name}>{name}</Title>
          </TitleLink>

          <MetaRow>
            <MetaItem>
              <PersonIcon />
              <MetaText>{from}</MetaText>
            </MetaItem>

            <MetaItem>
              <TimeIcon />
              <MetaText>{days} Tage verbleibend</MetaText>
            </MetaItem>
          </MetaRow>

          <ProgressArea>
            <ProgressBar percentage={percentage} className="progress_bar_lg" />
          </ProgressArea>

          <BottomArea>
            {is_patreon ? (
              <SupporterBlock>
                <SupporterImg src={SupporterIcon} alt="supporter-icon" />
                <BottomText>{Number(donations_count)} Supporter</BottomText>
              </SupporterBlock>
            ) : is_ticketable ? (
              <SupporterBlock>
                <SupporterImg src={SupporterIcon} alt="supporter-icon" />
                <BottomText>
                  {Number(donations_count)} Tickets verkauft
                </BottomText>
              </SupporterBlock>
            ) : (
              <FundingBlock>
                <FundingValue>{formatMoney(fundings)} €</FundingValue>
                <FundingSubtext>
                  finanziert von {formatMoney(target)} €
                </FundingSubtext>
              </FundingBlock>
            )}

            {showDonationButton ? (
              <DonationButtonWrapper>
                <Link
                  style={{color: "#fff"}}
                  to={{
                    pathname: `/project/${id}/donation`,
                    search: `?donation=30&name=${name}`, // You can also pass query params
                    state: { finalUrl }, // Passing state
                  }}
                >
                  Unterstützen
                </Link>
              </DonationButtonWrapper>
            ) : null}
          </BottomArea>
        </Content>
      </CardWrapper>
    </OuterWrapper>
  );
}

MoreProjectCard.propTypes = {
  project: PropTypes.object.isRequired,
  isSuccessful: PropTypes.bool,
};

const OuterWrapper = styled.div`
  height: 100%;
`;

const CardWrapper = styled.article`
  cursor: pointer;
  width: 100%;
  height: 100%;
  position: relative;
`;

const BaseLink = styled(Link)`
  margin: 0;
  color: #1e322b;

  &:hover {
    color: #1e322b;
    border-bottom: none;
  }
`;

const TopLink = styled(BaseLink)`
  display: block;
`;

const TitleLink = styled(BaseLink)`
  display: block;
`;

const ImageWrapper = styled.div`
  position: relative;
  max-height: 240px;
  border-radius: 0px 16px 0 16px;
  overflow: hidden;

  @media ${device.laptop} {
    max-height: 150px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 240px;
  display: block;
  background: ${Colors.grey};
  object-fit: cover;
  border-radius: 0px 16px 0 16px;

  @media ${device.laptop} {
    height: 150px;
  }
`;

const CategoryBadge = styled.p`
  color: ${Colors.white};
  text-transform: uppercase;
  font-size: 12px;
  background-color: rgba(42, 42, 42, 0.8);
  padding: 5px;
  display: inline-block;
  position: absolute;
  bottom: 0;
  left: 0;
  margin: 0;
  opacity: 0.8;
`;

const Content = styled.div`
    margin-top: 12px;
`;

const Title = styled.h2`
 
  font-family: 'Inter',sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
    text-align: left;
  color: #181d27;
  height: 60px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  
`;

const MetaRow = styled.div`
  display: flex;
  gap: 18px;
  margin-bottom: 10px;
  text-align: left;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;


const MetaText = styled.p`
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter',sans-serif;
  margin: 0;
  color: #656565;
`;

const ProgressArea = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
`;

const BottomArea = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const SupporterBlock = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const SupporterImg = styled.img`
  width: 15px;
  min-width: 15px;
  height: 15px;
  margin-top: 5px;
`;

const BottomText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1;
`;

const FundingBlock = styled.div`
  text-align: left;
`;

const FundingValue = styled.div`
  /* Heading */
  

  /* Text xl/Semibold */
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
  color: #181d27;
  
`;

const FundingSubtext = styled.div`
  
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  /* identical to box height, or 143% */

  color: #181d27;

 
`;

const DonationButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 12px 18px;
  gap: 6px;
  
  width: 142px;
  height: 48px;

  background: #9dbc60;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  border-radius: 8px;
  
  
`;
